import React, { useState, useEffect } from 'react';
import { signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { auth } from '../firebase';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { 
  ShieldCheck, 
  Clock, 
  BarChart3, 
  Users, 
  ArrowRight, 
  Search,
  CheckCircle2,
  Zap,
  Lock,
  DollarSign,
  UtensilsCrossed,
  Coffee,
  Wine,
  Scissors,
  Car,
  Package,
  Dumbbell,
  ShoppingBag,
  Gamepad2,
  Ticket,
  Droplets,
  Plane,
  TrendingDown,
  Menu,
  X,
  Smartphone,
  Play,
  Calculator,
  RefreshCw,
  Sparkles
} from 'lucide-react';
import { motion } from 'motion/react';
import { useFirebase } from '../contexts/FirebaseContext';
import { WaitlistForm } from './WaitlistForm';
import { AboutUs } from './AboutUs';
import { PublicInteractiveTools } from './PublicInteractiveTools';
import { InteractiveWalkthrough } from './InteractiveWalkthrough';
import { IntegrationsShowcase } from './IntegrationsShowcase';
import { RoiCalculator } from './RoiCalculator';
import { CoreIntelligenceEngines } from './CoreIntelligenceEngines';
import { TipPoolCalculator } from './TipPoolCalculator';

export const LandingPage: React.FC = () => {
  const { user } = useFirebase();
  const navigate = useNavigate();
  const [allocationMethod, setAllocationMethod] = useState<'equal' | 'role'>('equal');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showPublicTour, setShowPublicTour] = useState(false);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  useEffect(() => {
    const invite = searchParams.get('invite');
    if (invite) {
      sessionStorage.setItem('pendingInvite', invite);
    }
    const promoterRef = searchParams.get('ref') || searchParams.get('referral') || searchParams.get('code');
    if (promoterRef) {
      sessionStorage.setItem('pendingReferral', promoterRef.toUpperCase().trim());
    }
  }, [searchParams]);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "TipValue AI",
    "operatingSystem": "Web",
    "applicationCategory": "BusinessApplication",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "ratingCount": "127"
    },
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "description": "Automate tip pooling, distribution, and reconciliation for service-based teams. Simplify payroll and ensure fair allocations for restaurants, salons, retail, and beyond."
  };

  const handleLogin = async () => {
    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
    } catch (err: any) {
      console.error("Login failed:", err);
      if (err.code === 'auth/popup-blocked') {
        alert("Pop-up blocked. Please enable pop-ups for this site to log in.");
      } else {
        alert("Login failed. Please try again or check your internet connection.");
      }
    }
  };

  return (
    <main id="main-content" className="min-h-screen bg-white overflow-x-hidden pt-[88px]">
      <script type="application/ld+json">
        {JSON.stringify(jsonLd)}
      </script>
      {/* Navigation */}
      <nav className="flex items-center justify-between px-4 md:px-12 py-6 border-b border-gray-100 fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-md z-50 shadow-sm">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center text-white shrink-0">
            <DollarSign size={20} />
          </div>
          <span className="font-black text-lg md:text-xl tracking-tighter uppercase italic">TipValue AI</span>
          <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 text-[10px] font-black uppercase tracking-wider">
            BETA
          </span>
        </div>
        <div className="hidden md:flex items-center gap-6 text-[10px] font-black uppercase tracking-widest text-gray-500">
          <a href="/" className="hover:text-blue-600 transition-colors">Home</a>
          <a href="#features" className="hover:text-blue-600 transition-colors">Features</a>
          <a href="#tools" className="hover:text-blue-600 transition-colors">Tools & ROI</a>
          <a href="#industry" className="hover:text-blue-600 transition-colors">Industry</a>
          <a href="#pricing" className="hover:text-blue-600 transition-colors">Pricing</a>
          <a href="#our-story" className="hover:text-blue-600 transition-colors">About</a>
          <a href="#contact" className="hover:text-blue-600 transition-colors">Contact</a>
        </div>
        <div className="flex items-center gap-2 md:gap-3">
          <button 
            id="btn-nav-live-demo"
            onClick={() => setShowPublicTour(true)}
            className="px-3 md:px-5 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 rounded-full text-[10px] font-black uppercase tracking-widest transition-all flex items-center gap-1.5 active:scale-95 whitespace-nowrap shadow-xs"
          >
            <Play size={12} className="fill-blue-700" />
            <span className="hidden sm:inline">Try</span> Interactive Demo
          </button>
          <button 
            id="btn-nav-sign-in"
            onClick={handleLogin}
            className="px-4 md:px-6 py-2 bg-gray-900 text-white rounded-full text-[10px] font-black uppercase tracking-widest hover:bg-blue-600 transition-all active:scale-95 whitespace-nowrap"
          >
            Sign In
          </button>
          
          {/* Mobile Menu Button */}
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-gray-700 hover:text-blue-600 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Toggle navigation menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      {/* Mobile Navigation Drawer */}
      {mobileMenuOpen && (
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="md:hidden fixed top-[81px] left-0 right-0 bg-white border-b border-gray-100 shadow-xl z-30 p-6 flex flex-col gap-4"
        >
          <a 
            href="/" 
            onClick={() => setMobileMenuOpen(false)}
            className="text-xs font-black uppercase tracking-widest text-gray-600 hover:text-blue-600 py-2 border-b border-gray-50 transition-colors"
          >
            Home
          </a>
          <a 
            href="#features" 
            onClick={() => setMobileMenuOpen(false)}
            className="text-xs font-black uppercase tracking-widest text-gray-600 hover:text-blue-600 py-2 border-b border-gray-50 transition-colors"
          >
            Features
          </a>
          <a 
            href="#tools" 
            onClick={() => setMobileMenuOpen(false)}
            className="text-xs font-black uppercase tracking-widest text-gray-600 hover:text-blue-600 py-2 border-b border-gray-50 transition-colors"
          >
            Tools & ROI
          </a>
          <a 
            href="#industry" 
            onClick={() => setMobileMenuOpen(false)}
            className="text-xs font-black uppercase tracking-widest text-gray-600 hover:text-blue-600 py-2 border-b border-gray-50 transition-colors"
          >
            Industry
          </a>
          <a 
            href="#pricing" 
            onClick={() => setMobileMenuOpen(false)}
            className="text-xs font-black uppercase tracking-widest text-gray-600 hover:text-blue-600 py-2 border-b border-gray-50 transition-colors"
          >
            Pricing
          </a>
          <a 
            href="#our-story" 
            onClick={() => setMobileMenuOpen(false)}
            className="text-xs font-black uppercase tracking-widest text-gray-600 hover:text-blue-600 py-2 border-b border-gray-50 transition-colors"
          >
            About
          </a>
          <a 
            href="#contact" 
            onClick={() => setMobileMenuOpen(false)}
            className="text-xs font-black uppercase tracking-widest text-gray-600 hover:text-blue-600 py-2 transition-colors"
          >
            Contact Us
          </a>
        </motion.div>
      )}

      {/* Hero Section */}
      <section className="px-6 pt-12 md:pt-20 pb-24 md:pb-32 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-8 text-center lg:text-left">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-100 rounded-full text-[10px] font-bold text-blue-600 uppercase tracking-widest"
            >
              <Zap size={14} className="animate-pulse" />
              TipValue Beta • Powered by GrowEzy.ai
            </motion.div>
            
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-4xl md:text-7xl font-black text-gray-900 leading-[0.9] tracking-tighter uppercase italic"
            >
              ELIMINATE <br />
              <span className="text-blue-600">THE HEADACHE.</span>
            </motion.h1>

            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-lg md:text-xl text-gray-500 font-medium max-w-xl mx-auto lg:mx-0 leading-relaxed"
            >
              Automated tip pooling and distribution built for all industries requiring tip processing. Save 5+ hours on payroll every week and reduce team disputes to zero.
            </motion.p>
            <div className="py-4">
              <WaitlistForm />
            </div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3 md:gap-4"
            >
              <button 
                id="btn-hero-get-started"
                onClick={handleLogin}
                className="w-full sm:w-auto px-8 py-4 bg-blue-600 text-white rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-blue-700 transition-all shadow-xl shadow-blue-600/30 active:scale-[0.98] text-center"
              >
                Get Started Free
              </button>
            </motion.div>
          </div>

          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="relative w-full max-w-lg mx-auto lg:max-w-none"
          >
            <div className="relative">
              <TipPoolCalculator onGetStarted={handleLogin} compact={true} />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Problem vs. Solution Board */}
      <section className="py-24 bg-white border-t border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center space-y-4 mb-16">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-rose-50 border border-rose-100 rounded-full text-[9px] font-black text-rose-600 uppercase tracking-widest">
              The Reality of Manual Pools
            </div>
            <h2 className="text-3xl md:text-5xl font-black text-gray-900 tracking-tighter uppercase italic">
              CONFRONTING THE SPREADSHEET CHAOS
            </h2>
            <p className="text-gray-500 font-medium max-w-xl mx-auto text-xs md:text-sm">
              Why relying on manual calculations, pen-and-paper notes, and text threads costs you hours and fractures workforce trust.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            
            {/* SPREADSHEET HELL (Problem) */}
            <div className="p-8 md:p-12 bg-rose-50/40 rounded-[2.5rem] border border-rose-100 space-y-6">
              <div className="flex items-center gap-2 text-rose-600">
                <TrendingDown className="w-6 h-6 animate-pulse" />
                <h3 className="text-xl font-black uppercase tracking-tight italic">Old Method: Manual Spreadsheets</h3>
              </div>
              <p className="text-gray-500 text-xs font-semibold">
                Errors in manual math create legal liabilities and lead to constant staff disputes at checkout.
              </p>
              
              <ul className="space-y-4 pt-2">
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center shrink-0 mt-0.5 font-bold text-xs">×</div>
                  <div>
                    <span className="font-extrabold text-xs text-gray-800 uppercase tracking-tight block">5+ Hours Wasted Each Pay Cycle</span>
                    <span className="text-[11px] text-gray-500 leading-normal">Tedious re-entry of clock-in hours, complex role percentages, and chasing missing cash jar records.</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center shrink-0 mt-0.5 font-bold text-xs">×</div>
                  <div>
                    <span className="font-extrabold text-xs text-gray-800 uppercase tracking-tight block">End-of-Shift Dispute Friction</span>
                    <span className="text-[11px] text-gray-500 leading-normal">Opaque tip math leads to server skepticism, checkout tension, and unnecessary team distrust.</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center shrink-0 mt-0.5 font-bold text-xs">×</div>
                  <div>
                    <span className="font-extrabold text-xs text-gray-800 uppercase tracking-tight block">FLSA & Labor Audit Vulnerability</span>
                    <span className="text-[11px] text-gray-500 leading-normal">Unrecorded tip pooling rules and missing historical logs leave operators exposed during audits.</span>
                  </div>
                </li>
              </ul>
            </div>

            {/* TipValue AI AUTOMATION (Solution) */}
            <div className="p-8 md:p-12 bg-blue-50/40 rounded-[2.5rem] border border-blue-100 space-y-6">
              <div className="flex items-center gap-2 text-blue-600">
                <ShieldCheck className="w-6 h-6" />
                <h3 className="text-xl font-black uppercase tracking-tight italic">The Future: TipValue Automation</h3>
              </div>
              <p className="text-gray-500 text-xs font-semibold">
                A structured, auditable system that calculates exact tip shares, secures compliance, and exports to payroll.
              </p>
              
              <ul className="space-y-4 pt-2">
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center shrink-0 mt-0.5 font-bold text-xs">✓</div>
                  <div>
                    <span className="font-extrabold text-xs text-gray-800 uppercase tracking-tight block">Streamlined Shift Workflows</span>
                    <span className="text-[11px] text-gray-600 leading-normal">Seamlessly map POS tip imports, calculate shift allocations against clock-in hours, and export payroll files.</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center shrink-0 mt-0.5 font-bold text-xs">✓</div>
                  <div>
                    <span className="font-extrabold text-xs text-gray-800 uppercase tracking-tight block">Complete Team Transparency</span>
                    <span className="text-[11px] text-gray-600 leading-normal">Detailed shift summaries and clear breakdown views give servers, bartenders, and support staff full confidence.</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center shrink-0 mt-0.5 font-bold text-xs">✓</div>
                  <div>
                    <span className="font-extrabold text-xs text-gray-800 uppercase tracking-tight block">Rigorous Audit & Compliance Trail</span>
                    <span className="text-[11px] text-gray-600 leading-normal">Permanent shift ledgers, FLSA manager safeguards, and Form 8027 shortfall tools keep you audit-ready.</span>
                  </div>
                </li>
              </ul>
            </div>

          </div>
        </div>
      </section>

      {/* Core Intelligence Engines Showcase */}
      <CoreIntelligenceEngines onOpenDemo={() => setShowPublicTour(true)} />

      {/* POS Ecosystem & Payroll CSV Showcase */}
      <IntegrationsShowcase onOpenDemo={() => setShowPublicTour(true)} />

      {/* Interactive Compliance Tools & ROI Calculator Suite */}
      <div id="tools" className="scroll-mt-24">
        <RoiCalculator onGetStarted={handleLogin} />
        <div className="py-20 bg-white">
          <PublicInteractiveTools />
        </div>
      </div>

      {/* Allocation Scenario Example */}
      <section className="py-32 bg-gray-900 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-500/10 border border-blue-500/20 rounded-full text-[10px] font-black text-blue-400 uppercase tracking-widest">
                The Allocation Engine
              </div>
              <h2 className="text-4xl md:text-6xl font-black text-white leading-none tracking-tighter uppercase italic">
                METHOD FIRST. <br />
                <span className="text-blue-500 uppercase">PRECISION ALWAYS.</span>
              </h2>
              <p className="text-lg text-gray-400 font-medium leading-relaxed">
                Choose your distribution method. Our engine calculates the initial role-shares first, then pro-rates every cent using exact labor hours captured from your POS or time-tracking tool.
              </p>
              
              <div className="flex gap-4 pt-4">
                <button 
                  onClick={() => setAllocationMethod('equal')}
                  className={`px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all ${allocationMethod === 'equal' ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30' : 'bg-gray-800 text-gray-500 hover:text-white'}`}
                >
                  Method 1: Equal Split
                </button>
                <button 
                  onClick={() => setAllocationMethod('role')}
                  className={`px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all ${allocationMethod === 'role' ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30' : 'bg-gray-800 text-gray-500 hover:text-white'}`}
                >
                  Method 2: % By Role
                </button>
              </div>
              <div className="text-[10px] font-black text-gray-500 uppercase tracking-widest flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" />
                Plus 4+ additional logic combinations including shift-based splits.
              </div>
            </div>

            <div className="relative">
              <div className="p-8 md:p-12 bg-white rounded-[3rem] shadow-2xl relative z-10 space-y-8">
                <div className="flex items-center justify-between border-b border-gray-100 pb-6">
                  <div>
                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Shift Total Pool</span>
                    <h3 className="text-4xl font-black text-gray-900 tracking-tighter">$200.00</h3>
                  </div>
                  <div className="px-4 py-2 bg-gray-50 border border-gray-100 rounded-2xl text-center">
                    <span className="block text-[8px] font-black text-gray-400 uppercase tracking-widest">Active Logic</span>
                    <span className="text-[10px] font-black text-blue-600 uppercase">
                      {allocationMethod === 'equal' ? 'Hours Only' : 'Role % then Hours'}
                    </span>
                  </div>
                </div>

                <div className="space-y-6">
                  {allocationMethod === 'equal' ? (
                    <>
                      <AllocationRow name="Sarah M." role="Bartender" hours="8.0" detail="Full Shift" amount="80.00" percent={40} color="bg-blue-600" />
                      <AllocationRow name="Jason K." role="Server" hours="8.0" detail="Full Shift" amount="80.00" percent={40} color="bg-gray-900" />
                      <AllocationRow name="Elena R." role="Server" hours="4.0" detail="Late Arrival (50%)" amount="40.00" percent={20} color="bg-gray-400" tag="Late Arrival" />
                    </>
                  ) : (
                    <>
                      <AllocationRow name="Sarah M." role="Bartender" hours="8.0" detail="50% Pool / Full Shift" amount="100.00" percent={50} color="bg-blue-600" />
                      <AllocationRow name="Jason K." role="Server" hours="8.0" detail="50% Pool / Full Shift" amount="66.67" percent={33.33} color="bg-gray-900" />
                      <AllocationRow name="Elena R." role="Server" hours="4.0" detail="50% Pool / Leaves Early" amount="33.33" percent={16.67} color="bg-gray-400" tag="Leaves Early" />
                    </>
                  )}
                </div>

                <div className="bg-blue-50 p-6 rounded-3xl border border-blue-100">
                  <p className="text-[10px] font-black text-blue-900 uppercase tracking-widest leading-relaxed">
                    <span className="text-blue-600">Observation:</span> {allocationMethod === 'equal' 
                      ? "In an Equal Split, Jason and Sarah earn the same amount despite role differences because they worked equal hours."
                      : "With % By Role, the Bartender receives the dedicated 50% pool, while Servers split their remaining 50% pool proportionally by time worked."}
                  </p>
                </div>
              </div>

              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-blue-600/10 rounded-full blur-[100px] pointer-events-none" />
            </div>
          </div>
        </div>
      </section>


      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-gray-50 rounded-[3rem] p-12 border border-gray-100 flex flex-col md:flex-row items-center justify-between gap-10">
             <div className="space-y-4 text-center md:text-left">
                <h3 className="text-3xl font-black text-gray-900 uppercase italic tracking-tighter">Ready to try it out?</h3>
                <p className="text-gray-500 font-medium">Start your 30-day free trial. No credit card required.</p>
             </div>
             <button 
              onClick={handleLogin}
              className="w-full md:w-auto px-10 py-5 bg-gray-900 text-white rounded-2xl font-black uppercase tracking-widest text-sm hover:bg-blue-600 transition-all active:scale-95 shadow-xl"
            >
              Get Started Free
            </button>
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section id="industry" className="py-24 bg-white scroll-mt-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 tracking-tighter uppercase italic">Supported Industries</h2>
            <p className="text-gray-500 font-medium max-w-2xl mx-auto italic">Industry-leading tip allocation software providing precision pooling for any service team that relies on shared tips, service fees, or gratuity pools.</p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            <IndustryCard icon={<UtensilsCrossed size={20} />} name="Restaurants" />
            <IndustryCard icon={<Wine size={20} />} name="Bars & Clubs" />
            <IndustryCard icon={<Coffee size={20} />} name="Cafes" />
            <IndustryCard icon={<Scissors size={20} />} name="Salons & Spas" />
            <IndustryCard icon={<Gamepad2 size={20} />} name="Bowling Alleys" />
            <IndustryCard icon={<Ticket size={20} />} name="Entertainment" />
            <IndustryCard icon={<Droplets size={20} />} name="Car Washes" />
            <IndustryCard icon={<Car size={20} />} name="Valet & Parking" />
            <IndustryCard icon={<Dumbbell size={20} />} name="Fitness & Yoga" />
            <IndustryCard icon={<Package size={20} />} name="Logistics" />
            <IndustryCard icon={<Plane size={20} />} name="Travel & Hospitality" />
            <IndustryCard icon={<ShoppingBag size={20} />} name="Luxury Retail" />
          </div>
        </div>
      </section>

      <section className="py-32 bg-gray-50/50">
        <div className="max-w-7xl mx-auto px-6">
           <div className="text-center space-y-4 mb-16">
              <h2 className="text-4xl md:text-5xl font-black text-gray-900 tracking-tighter uppercase italic">Optimized for Every Role</h2>
              <p className="text-gray-500 font-medium max-w-2xl mx-auto italic">Tailored experiences that empower employees, managers, and owners with the exact tools needed for accountability and efficiency.</p>
            </div>
           <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="p-12 bg-white rounded-[3rem] border border-gray-100 space-y-6">
                 <div className="w-12 h-12 bg-blue-600 rounded-2xl flex items-center justify-center text-white">
                    <Users size={24} />
                 </div>
                 <h3 className="text-3xl font-black text-gray-900 uppercase italic tracking-tighter">Managers</h3>
                 <p className="text-gray-500 font-medium leading-relaxed">
                    Automate complex allocations with pro-level precision. Review shifts, approve tip payroll extracts, and manage disputes from one easy-to-use dashboard.
                 </p>
                 <ul className="space-y-3 pt-4">
                    {["POS Credit Card Imports", "Jar & Employee Cash Submittals", "Allocation Engine Control", "Immutable Auditing", "CSV Payroll Extract"].map(f => (
                      <li key={f} className="flex items-center gap-3 text-[10px] font-black uppercase tracking-widest text-gray-700">
                        <div className="w-1.5 h-1.5 bg-blue-600 rounded-full" />
                        {f}
                      </li>
                    ))}
                 </ul>
              </div>
              <div className="p-12 bg-white rounded-[3rem] border border-gray-100 space-y-6">
                 <div className="w-12 h-12 bg-emerald-600 rounded-2xl flex items-center justify-center text-white">
                    <Zap size={24} />
                 </div>
                 <h3 className="text-3xl font-black text-gray-900 uppercase italic tracking-tighter">Employees</h3>
                 <p className="text-gray-500 font-medium leading-relaxed">
                    Full visibility into every tip you earn. Track your performance, verify your allocations, and resolve discrepancies instantly with our mobile-first portal.
                 </p>
                 <ul className="space-y-3 pt-4">
                    {["Tip submission (Cash)", "Real-time tip earnings tracking", "Dispute resolution portal", "Transparent distribution history"].map(f => (
                      <li key={f} className="flex items-center gap-3 text-[10px] font-black uppercase tracking-widest text-gray-700">
                        <div className="w-1.5 h-1.5 bg-emerald-600 rounded-full" />
                        {f}
                      </li>
                    ))}
                 </ul>
              </div>
              <div className="p-12 bg-white rounded-[3rem] border border-gray-100 space-y-6">
                 <div className="w-12 h-12 bg-purple-600 rounded-2xl flex items-center justify-center text-white">
                    <CheckCircle2 size={24} />
                 </div>
                 <h3 className="text-3xl font-black text-gray-900 uppercase italic tracking-tighter">Owners</h3>
                 <p className="text-gray-500 font-medium leading-relaxed">
                    High-level visibility, automated reconciliation, and actionable insights to monitor operational performance and tip liability.
                 </p>
                 <ul className="space-y-3 pt-4">
                    {["Automated reconciliation reports", "Global performance visibility (Coming Soon)", "Tip cashflow forecast", "Performance Dashboard"].map(f => (
                      <li key={f} className="flex items-center gap-3 text-[10px] font-black uppercase tracking-widest text-gray-700">
                        <div className="w-1.5 h-1.5 bg-purple-600 rounded-full" />
                        {f}
                      </li>
                    ))}
                 </ul>
              </div>
           </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-32 bg-gray-50/50 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center space-y-4 mb-24">
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 tracking-tighter uppercase italic">Simple, Transparent Pricing</h2>
            <p className="text-gray-500 font-medium max-w-2xl mx-auto">Choose the plan that fits your venue size.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <PricingCard 
                title="Base Plan" 
                price="$19.99" 
                features={[
                  "1 Store Location",
                  "Tip Jar & Personal Cash Reporting",
                  "POS CSV Shift & Hours Import",
                  "Tip Allocations (Hours & Points)",
                  "Automated Flux & Reconciliations",
                  "Employee Approvals & Disputes",
                  "IRS Form 4070 Cash Reporting",
                  "Standard Tip Payroll CSV Export",
                  "IRS 8% Gross Receipts Shortfall"
                ]} 
                onAction={handleLogin} 
                subtext="/mo (+ $6.00/user)"
            />
            <PricingCard 
                title="Enhanced Plan" 
                price="$39.99" 
                features={[
                  "Up to 3 Store Locations",
                  "Base Plan inclusions",
                  "Benchmark Dashboard & Trends",
                  "Team Earning Velocity",
                  "Shift Performance Analytics",
                  "Multi-Store Batch Payroll Extracts"
                ]} 
                onAction={handleLogin} 
                subtext="/mo (+ $5.00/user)"
            />
            <PricingCard 
                title="Enterprise Plan" 
                price="$79.99" 
                features={[
                  "Up to 5 Store Locations",
                  "Enhanced Plan inclusions",
                  "Cross-Store Executive Rollups",
                  "Raw, Unformatted Data Extracts",
                  "Custom Payroll Formats (Coming Soon)",
                  "REST API & Webhooks (Coming Soon)",
                  "Priority Dedicated Support & Onboarding"
                ]} 
                onAction={handleLogin} 
                subtext="/mo (+ $3.50/user)"
            />
          </div>
        </div>
      </section>

      {/* Ease of Use CTA */}
      <section className="py-32 bg-gray-900 border-t border-gray-800">
        <div className="max-w-4xl mx-auto px-6 text-center space-y-12">
           <div className="space-y-4">
              <h2 className="text-5xl md:text-7xl font-black text-white tracking-tighter leading-none uppercase italic">Ready to optimize?</h2>
              <p className="text-xl text-gray-400 font-medium">Join hundreds of venues managing tips with zero friction.</p>
           </div>
           <button 
             onClick={handleLogin}
             className="w-full sm:w-auto px-12 py-6 bg-white text-gray-900 rounded-3xl font-black uppercase tracking-[0.2em] hover:bg-blue-500 hover:text-white transition-all active:scale-95 text-lg text-center"
           >
             Launch Dashboard
           </button>
        </div>
      </section>

      <AboutUs />
      

      {/* Contact Us Section */}
      <section id="contact" className="py-24 bg-white border-t border-gray-100 scroll-mt-24">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center space-y-4 mb-12">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-50 border border-blue-100 rounded-full text-[9px] font-black text-blue-600 uppercase tracking-widest">
              Have Questions?
            </div>
            <h2 className="text-3xl md:text-5xl font-black text-gray-900 tracking-tighter uppercase italic">
              CONTACT OUR TEAM
            </h2>
            <p className="text-gray-500 font-medium max-w-lg mx-auto">
              Ready to automate your tip management? Get in touch with us for enterprise integration, custom feature requests, or direct support.
            </p>
          </div>

          <form onSubmit={(e) => { e.preventDefault(); alert("Thank you! Your message has been sent successfully. We will get back to you shortly."); }} className="space-y-6 bg-gray-50 p-8 md:p-12 rounded-[2.5rem] border border-gray-100">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-gray-500 uppercase tracking-widest px-1">Your Name</label>
                <input required type="text" placeholder="John Doe" className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:border-blue-600 font-medium text-sm transition-colors" />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-gray-500 uppercase tracking-widest px-1">Business Email</label>
                <input required type="email" placeholder="john@restaurant.com" className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:border-blue-600 font-medium text-sm transition-colors" />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-bold text-gray-500 uppercase tracking-widest px-1">How can we help?</label>
              <textarea required rows={4} placeholder="Tell us about your tip pooling requirements..." className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:border-blue-600 font-medium text-sm transition-colors resize-none" />
            </div>
            <button type="submit" className="w-full py-4 bg-blue-600 text-white rounded-xl font-black uppercase tracking-widest text-sm hover:bg-blue-700 transition-all shadow-xl shadow-blue-600/20 active:scale-95">
              Send Message
            </button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-gray-950 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-white/10 rounded flex items-center justify-center text-white">
              <DollarSign size={14} />
            </div>
            <span className="font-black text-white tracking-tighter uppercase italic text-sm">TipValue AI</span>
          </div>
          <div className="flex flex-wrap gap-6 text-[10px] font-black uppercase tracking-widest text-gray-500">
            <button onClick={() => setShowPublicTour(true)} className="hover:text-blue-400 uppercase text-blue-500 cursor-pointer">Live Demo</button>
            <a href="#features" className="hover:text-white">Features</a>
            <a href="#tools" className="hover:text-white">Tools & ROI</a>
            <button onClick={handleLogin} className="hover:text-white uppercase">App Login</button>
            <Link to="/privacy" className="hover:text-white">Privacy</Link>
            <Link to="/terms" className="hover:text-white">Terms</Link>
            <Link to="/saas-agreement" className="hover:text-white">SaaS</Link>
          </div>
          <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest text-center md:text-right">
            © {new Date().getFullYear()} TipValue AI • A Product of GrowEzi AI (d/b/a Data Driven Bookkeeping LLC)
          </p>
        </div>
      </footer>

      {/* Public Interactive Walkthrough Modal */}
      {showPublicTour && (
        <InteractiveWalkthrough onClose={() => setShowPublicTour(false)} />
      )}

    </main>
  );
};

const PricingCard = ({ title, price, features, subtext, onAction }: any) => (
  <div className="p-8 bg-white rounded-[2.5rem] border border-gray-100 flex flex-col hover:shadow-2xl hover:shadow-blue-500/10 transition-all group">
    <h3 className="text-xl font-black text-gray-900 uppercase italic tracking-tight">{title}</h3>
    <div className="text-4xl font-black text-gray-900 my-6">
      {price}
      <span className="text-sm text-gray-400 font-bold ml-1">{subtext}</span>
    </div>
    <ul className="space-y-4 mb-8 flex-grow">
      {features.map((f: string) => (
        <li key={f} className="flex items-center gap-3 text-sm text-gray-600 font-medium">
          <CheckCircle2 size={16} className="text-blue-600" />
          {f}
        </li>
      ))}
    </ul>
    <button 
      onClick={onAction}
      className="w-full py-4 bg-gray-900 text-white rounded-2xl font-black uppercase tracking-widest text-sm hover:bg-blue-600 transition-all"
    >
      Get Started
    </button>
  </div>
);

const FeatureCard = ({ icon, title, description, tag, color }: any) => (
  <div className="p-8 bg-white rounded-[2.5rem] border border-gray-100 hover:shadow-2xl hover:shadow-blue-500/10 transition-all group">
    <div className={`p-4 rounded-2xl w-fit mb-6 ${color}`}>
      {icon}
    </div>
    <div className="space-y-2">
      <div className="inline-block px-2 py-0.5 bg-gray-100 rounded text-[8px] font-black uppercase text-gray-600 tracking-widest mb-1">{tag}</div>
      <h3 className="text-xl font-black text-gray-900 uppercase italic tracking-tight">{title}</h3>
      <p className="text-gray-500 text-sm leading-relaxed font-medium">{description}</p>
    </div>
  </div>
);

const IndustryCard = ({ icon, name }: any) => (
  <div className="group p-6 bg-gray-50 rounded-[2rem] border border-transparent hover:border-blue-100 hover:bg-white hover:shadow-xl hover:shadow-blue-500/5 transition-all flex flex-col items-center text-center space-y-4">
    <div className="p-3 bg-white rounded-xl text-gray-400 group-hover:text-blue-600 group-hover:scale-110 transition-all shadow-sm">
      {icon}
    </div>
    <span className="text-[10px] font-black uppercase tracking-widest text-gray-900">{name}</span>
  </div>
);

const AllocationRow = ({ name, role, hours, detail, amount, percent, color, tag }: any) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center text-gray-900 font-black text-xs">
          {name.charAt(0)}
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-black text-gray-900 uppercase tracking-tight">{name}</span>
            {tag && <span className="px-1.5 py-0.5 bg-amber-50 text-amber-600 text-[7px] font-black rounded uppercase tracking-widest">{tag}</span>}
          </div>
          <span className="text-[9px] font-bold text-gray-600 uppercase tracking-widest">{role} • {hours} hrs • {detail}</span>
        </div>
      </div>
      <div className="text-right">
        <span className="block text-sm font-black text-gray-900 tracking-tight">${amount}</span>
        <span className="text-[8px] font-black text-gray-600 uppercase">{percent}% share</span>
      </div>
    </div>
    <div className="w-full h-2 bg-gray-50 rounded-full overflow-hidden">
      <div 
        className={`h-full transition-all duration-1000 ${color}`} 
        style={{ width: `${percent}%` }}
      />
    </div>
  </div>
);
